import { useEffect, useContext, useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Alert } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MainSectionSection from "../../components/MainSectionSection";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: {
      init: (params: {
        appId: string;
        autoLogAppEvents: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: { authResponse?: { code: string } }) => void,
        params: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras: {
            setup: Record<string, unknown>;
            featureType: string;
            sessionInfoVersion: string;
          };
        }
      ) => void;
    };
  }
}

const FACEBOOK_APP_ID = "591872132829806";
const FACEBOOK_CONFIG_ID = "716569384065478";

const getMessagingLimitLabel = (limit?: string): string => {
  const limitLabels: Record<string, string> = {
    TIER_1K: "1K/dia",
    TIER_10K: "10K/dia",
    TIER_100K: "100K/dia",
    TIER_UNLIMITED: "Ilimitado",
    STANDARD: "1K/dia",
  };
  return limit ? limitLabels[limit] || limit : "N/A";
};

const getQualityRatingColor = (rating?: string): string => {
  if (rating === "GREEN") return "bg-green-100 text-green-800";
  if (rating === "YELLOW") return "bg-yellow-100 text-yellow-800";
  if (rating === "RED") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-800";
};

const DefaultPipelineSelect = ({
  value,
  pipelines,
  disabled,
  onChange,
}: {
  value?: string;
  pipelines: { id: string; name: string }[];
  disabled?: boolean;
  onChange: (pipelineId: string) => void;
}) => (
  <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-8 w-full min-w-40 text-xs">
      <SelectValue placeholder="Sin asignar" />
    </SelectTrigger>
    <SelectContent>
      {pipelines.map((pipeline) => (
        <SelectItem key={pipeline.id} value={pipeline.id}>
          {pipeline.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const WhatsappConnectionsContainer = () => {
  const { merchantId } = useContext(MerchantContext);
  const isProcessingRef = useRef(false);
  const launchedFeatureTypeRef = useRef("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [connectionToToggle, setConnectionToToggle] = useState<{
    id: string;
    phoneNumber: string;
    disabled: boolean;
  } | null>(null);
  const hasSyncedRef = useRef(false);

  // Get user info to check email domain
  const { data: userInfo } = trpc.auth.getUserInfo.useQuery();

  // Check if user email domain includes "palomma"
  const isPalommaUser = userInfo?.email?.toLowerCase().includes("palomma") ?? false;

  const {
    data: connections,
    isLoading,
    refetch,
  } = trpc.whatsappConnections.list.useQuery({ merchantId }, { enabled: !!merchantId });

  const { data: pipelines } = trpc.pipeline.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const {
    data: availablePhoneNumbers,
    isLoading: isLoadingPhoneNumbers,
    refetch: refetchPhoneNumbers,
  } = trpc.whatsappConnections.getAvailablePhoneNumbers.useQuery(
    { merchantId },
    { enabled: false }
  );

  const createFromExistingPhone = trpc.whatsappConnections.createFromExistingPhone.useMutation({
    onSuccess: () => {
      toast.success("Conexion exitosa", {
        description: "El numero de WhatsApp ha sido conectado correctamente",
      });
      refetch();
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error("Error en la conexion", {
        description: error.message || "No se pudo conectar el numero de WhatsApp",
      });
    },
  });

  const createConnection = trpc.whatsappConnections.create.useMutation({
    onSuccess: () => {
      isProcessingRef.current = false;
      toast.success("Conexion exitosa", {
        description: "Tu numero de WhatsApp ha sido conectado correctamente",
      });
      refetch();
    },
    onError: (error) => {
      isProcessingRef.current = false;
      toast.error("Error en la conexion", {
        description: error.message || "No se pudo conectar el numero de WhatsApp",
      });
    },
  });

  const setDisabled = trpc.whatsappConnections.setDisabled.useMutation({
    onSuccess: (data) => {
      toast.success(data.disabled ? "Conexion deshabilitada" : "Conexion habilitada", {
        description: data.disabled
          ? "La conexion de WhatsApp ha sido deshabilitada. Los mensajes seguiran recibiendose pero el agente no respondera automaticamente."
          : "La conexion de WhatsApp ha sido habilitada. El agente respondera automaticamente a los mensajes.",
      });
      refetch();
      setIsConfirmDialogOpen(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo cambiar el estado de la conexion",
      });
    },
  });

  const setCoexistence = trpc.whatsappConnections.setCoexistence.useMutation({
    onSuccess: (data) => {
      toast.success(
        data.isCoexistence ? "Modo coexistencia activado" : "Modo coexistencia desactivado",
        {
          description: data.isCoexistence
            ? "Esta linea se tratara como una linea de asesor (WhatsApp Business app en el celular)."
            : "Esta linea se tratara como una linea del negocio: los mensajes entrantes crean leads.",
        }
      );
      refetch();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo cambiar el modo coexistencia",
      });
    },
  });

  const setPrimary = trpc.whatsappConnections.setPrimary.useMutation({
    onSuccess: (data) => {
      toast.success(data.isPrimary ? "Linea marcada como principal" : "Linea marcada como secundaria", {
        description: data.isPrimary
          ? "Esta linea ahora se puede asignar a un agente AI."
          : "Esta linea ya no se puede asignar a un agente AI.",
      });
      refetch();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo cambiar el tipo de la linea",
      });
    },
  });

  const setDefaultPipelineId = trpc.whatsappConnections.setDefaultPipelineId.useMutation({
    onSuccess: () => {
      toast.success("Embudo por defecto actualizado", {
        description: "Los nuevos leads de esta linea se crearan en este embudo.",
      });
      refetch();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo actualizar el embudo por defecto",
      });
    },
  });

  const syncPhoneStatus = trpc.whatsappConnections.syncPhoneStatus.useMutation({
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      console.error("Error syncing phone status:", error);
    },
  });

  // Auto-sync phone status when connections are loaded (once per page load)
  useEffect(() => {
    if (connections && connections.length > 0 && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      syncPhoneStatus.mutate({ merchantId });
    }
  }, [connections, merchantId, syncPhoneStatus]);

  // Reset sync flag when merchantId changes
  useEffect(() => {
    hasSyncedRef.current = false;
  }, [merchantId]);

  useEffect(() => {
    // Load Facebook SDK
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    document.body.appendChild(script);

    // SDK initialization
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: "v23.0",
      });
    };

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const fbLoginCallback = useCallback(
    (response: any) => {
      console.log("fbLoginCallback called", {
        isProcessing: isProcessingRef.current,
        hasAuthCode: !!response?.authResponse?.code,
        timestamp: new Date().toISOString(),
      });

      if (isProcessingRef.current) {
        console.warn("Already processing a connection request, ignoring duplicate call");
        return;
      }

      // Validate response has authorization code
      if (!response?.authResponse?.code) {
        console.warn("No authorization code in response, skipping connection creation", response);
        return;
      }

      console.log("Setting isProcessing to true and calling mutation");
      isProcessingRef.current = true;

      createConnection.mutate({
        merchantId,
        flowResponse: response,
        isCoexistence: launchedFeatureTypeRef.current === "whatsapp_business_app_onboarding",
      });
    },
    [merchantId, createConnection.mutate]
  );

  const launchWhatsAppFlow = useCallback(
    (featureType: string = "") => {
      if (isProcessingRef.current) {
        toast.warning("Conexion en proceso", {
          description: "Ya hay una conexion en proceso. Por favor espera a que termine.",
        });
        return;
      }

      launchedFeatureTypeRef.current = featureType;
      window.FB.login(fbLoginCallback, {
        config_id: FACEBOOK_CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType,
          sessionInfoVersion: "3",
        },
      });
    },
    [fbLoginCallback]
  );

  const handleOpenAdvancedConfig = useCallback(() => {
    refetchPhoneNumbers();
    setIsModalOpen(true);
  }, [refetchPhoneNumbers]);

  const handleConnectPhone = useCallback(
    (phoneNumberId: string, displayPhoneNumber: string, verifiedName: string) => {
      createFromExistingPhone.mutate({
        merchantId,
        phoneNumberId,
        displayPhoneNumber,
        verifiedName,
      });
    },
    [merchantId, createFromExistingPhone]
  );

  const handleToggleDisabled = useCallback(
    (connectionId: string, phoneNumber: string, currentDisabled: boolean) => {
      setConnectionToToggle({
        id: connectionId,
        phoneNumber,
        disabled: !currentDisabled,
      });
      setIsConfirmDialogOpen(true);
    },
    []
  );

  const handleConfirmToggle = useCallback(() => {
    if (!connectionToToggle || !userInfo?.email) return;

    setDisabled.mutate({
      merchantId,
      id: connectionToToggle.id,
      disabled: connectionToToggle.disabled,
      userEmail: userInfo.email,
    });
  }, [connectionToToggle, merchantId, userInfo?.email, setDisabled]);

  const handleToggleCoexistence = useCallback(
    (connectionId: string, currentIsCoexistence: boolean) => {
      setCoexistence.mutate({
        merchantId,
        id: connectionId,
        isCoexistence: !currentIsCoexistence,
      });
    },
    [merchantId, setCoexistence]
  );

  const handleTogglePrimary = useCallback(
    (connectionId: string, currentIsPrimary: boolean) => {
      setPrimary.mutate({
        merchantId,
        id: connectionId,
        isPrimary: !currentIsPrimary,
      });
    },
    [merchantId, setPrimary]
  );

  const handleDefaultPipelineChange = useCallback(
    (connectionId: string, pipelineId: string) => {
      setDefaultPipelineId.mutate({
        merchantId,
        id: connectionId,
        defaultPipelineId: pipelineId,
      });
    },
    [merchantId, setDefaultPipelineId]
  );

  return (
    <MainSectionSection>
      <div className="mb-6">
        <h2 className="text-xl font-bold md:text-2xl">
          Conexion con WhatsApp Business
        </h2>
      </div>

      {/* Existing Connections */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : connections && connections.length > 0 ? (
        <div className="mb-8">
          {/* Mobile card view */}
          <div className="rounded-md border border-gray-200 md:hidden">
            {connections.map((connection, index) => (
              <div
                key={connection.id}
                className={cn(
                  "w-full p-3",
                  index < connections.length - 1 && "border-b border-gray-200"
                )}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {connection.phoneNumber || "Sin numero"}
                  </span>
                  {connection.disconnected ? (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800">Desconectado</span>
                  ) : connection.isPrimary ? (
                    <div className="flex items-center gap-2">
                      <span className={cn("rounded-full px-2 py-0.5 text-xs", connection.disabled ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800")}>
                        {connection.disabled ? "Deshabilitado" : "Habilitado"}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!connection.disabled}
                        onClick={() =>
                          handleToggleDisabled(
                            connection.id,
                            connection.phoneNumber || connection.phoneNumberId,
                            connection.disabled ?? false
                          )
                        }
                        className={cn(
                          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                          !connection.disabled ? "bg-green-500" : "bg-gray-300"
                        )}
                      >
                        <span className={cn("pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform", !connection.disabled ? "translate-x-4" : "translate-x-0")} />
                      </button>
                    </div>
                  ) : (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">N/A</span>
                  )}
                </div>
                <div className="mb-1 flex flex-wrap gap-2">
                  {connection.isPrimary ? (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">Principal</span>
                  ) : (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">Secundario</span>
                  )}
                  <span className={cn("rounded-full px-2 py-0.5 text-xs", getQualityRatingColor(connection.qualityRating))}>
                    {connection.qualityRating || "N/A"}
                  </span>
                  <span className="text-xs text-gray-600">
                    {getMessagingLimitLabel(connection.messagingLimit)}
                  </span>
                </div>
                <p className="break-all font-mono text-xs text-gray-500">
                  ID: {connection.phoneNumberId}
                </p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="shrink-0 text-xs text-gray-500">Linea principal:</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={connection.isPrimary}
                    disabled={setPrimary.isLoading}
                    onClick={() => handleTogglePrimary(connection.id, connection.isPrimary)}
                    className={cn(
                      "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                      connection.isPrimary ? "bg-green-500" : "bg-gray-300"
                    )}
                  >
                    <span className={cn("pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform", connection.isPrimary ? "translate-x-4" : "translate-x-0")} />
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="shrink-0 text-xs text-gray-500">Modo coexistencia:</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={connection.isCoexistence}
                    disabled={setCoexistence.isLoading}
                    onClick={() =>
                      handleToggleCoexistence(connection.id, connection.isCoexistence)
                    }
                    className={cn(
                      "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                      connection.isCoexistence ? "bg-blue-600" : "bg-gray-300"
                    )}
                  >
                    <span className={cn("pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform", connection.isCoexistence ? "translate-x-4" : "translate-x-0")} />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="shrink-0 text-xs text-gray-500">Embudo por defecto:</span>
                  <DefaultPipelineSelect
                    value={connection.defaultPipelineId}
                    pipelines={pipelines ?? []}
                    disabled={setDefaultPipelineId.isLoading}
                    onChange={(pipelineId) =>
                      handleDefaultPipelineChange(connection.id, pipelineId)
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Numero de telefono</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    <div className="flex flex-col gap-0.5">
                      <span>Tipo</span>
                      <span className="text-[10px] font-normal normal-case leading-tight text-gray-400">
                        Sólo las líneas principales se pueden asignar a un agente AI
                      </span>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Calidad</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Limite de mensajes</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Estado</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    <div className="flex flex-col gap-0.5">
                      <span>Coexistencia</span>
                      <span className="text-[10px] font-normal normal-case leading-tight text-gray-400">
                        La línea usa la app de WhatsApp Business en un celular
                      </span>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    <div className="flex flex-col gap-0.5">
                      <span>Embudo por defecto</span>
                      <span className="text-[10px] font-normal normal-case leading-tight text-gray-400">
                        Sólo para líneas sin agente AI asociado
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {connections.map((connection) => (
                  <tr key={connection.id} className="border-b border-gray-100">
                    <td className="px-3 py-2">{connection.phoneNumber || "Sin numero"}</td>
                    <td className="px-3 py-2 font-mono text-sm">
                      {connection.phoneNumberId}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={connection.isPrimary}
                          disabled={setPrimary.isLoading}
                          onClick={() => handleTogglePrimary(connection.id, connection.isPrimary)}
                          className={cn(
                            "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                            connection.isPrimary ? "bg-green-500" : "bg-gray-300"
                          )}
                        >
                          <span className={cn("pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform", connection.isPrimary ? "translate-x-4" : "translate-x-0")} />
                        </button>
                        {connection.isPrimary ? (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">Principal</span>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">Secundario</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn("rounded-full px-2 py-0.5 text-xs", getQualityRatingColor(connection.qualityRating))}>
                        {connection.qualityRating || "N/A"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm">{getMessagingLimitLabel(connection.messagingLimit)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {connection.disconnected ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800">Desconectado</span>
                      ) : connection.isPrimary ? (
                        <div className="flex items-center gap-3">
                          <span className={cn("rounded-full px-2 py-0.5 text-xs", connection.disabled ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800")}>
                            {connection.disabled ? "Deshabilitado" : "Habilitado"}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!connection.disabled}
                            onClick={() =>
                              handleToggleDisabled(
                                connection.id,
                                connection.phoneNumber || connection.phoneNumberId,
                                connection.disabled ?? false
                              )
                            }
                            className={cn(
                              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                              !connection.disabled ? "bg-green-500" : "bg-gray-300"
                            )}
                          >
                            <span className={cn("pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform", !connection.disabled ? "translate-x-4" : "translate-x-0")} />
                          </button>
                        </div>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">N/A</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={connection.isCoexistence}
                        disabled={setCoexistence.isLoading}
                        onClick={() =>
                          handleToggleCoexistence(connection.id, connection.isCoexistence)
                        }
                        className={cn(
                          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                          connection.isCoexistence ? "bg-blue-600" : "bg-gray-300"
                        )}
                      >
                        <span className={cn("pointer-events-none block size-4 rounded-full bg-white shadow-sm transition-transform", connection.isCoexistence ? "translate-x-4" : "translate-x-0")} />
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <DefaultPipelineSelect
                        value={connection.defaultPipelineId}
                        pipelines={pipelines ?? []}
                        disabled={setDefaultPipelineId.isLoading}
                        onChange={(pipelineId) =>
                          handleDefaultPipelineChange(connection.id, pipelineId)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div>
        <Alert status="info" className="mb-6">
          <p className="text-sm">
            <strong>Importante:</strong> Para conectar una cuenta de WhatsApp, necesitas tener
            una cuenta de Facebook y ser administrador del portafolio comercial de tu empresa.
          </p>
        </Alert>

        <div className="flex flex-col gap-5">
          <p className="mb-1 text-lg font-bold">
            Flujos de conexion:
          </p>

          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-gray-200 p-4 transition-all hover:border-green-500 hover:shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start">
                <div className="flex flex-1 flex-col gap-2">
                  <p className="font-bold">Usar numero de Palomma</p>
                  <p className="text-sm text-gray-600">
                    Creas una cuenta de WhatsApp Business y Palomma crea un numero dentro de esa
                    cuenta.
                  </p>
                </div>
                <Button
                  onClick={() => launchWhatsAppFlow("only_waba_sharing")}
                  disabled={createConnection.isLoading}
                  className="min-h-11 w-full shrink-0 bg-green-500 text-white hover:bg-green-600 md:w-auto"
                >
                  {createConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
                  Conectar
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-gray-200 p-4 transition-all hover:border-green-500 hover:shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start">
                <div className="flex flex-1 flex-col gap-2">
                  <p className="font-bold">Usar tu propio numero</p>
                  <p className="text-sm text-gray-600">
                    Creas una cuenta de WhatsApp Business y agregas tu propio numero dentro de esa
                    cuenta.
                  </p>
                </div>
                <Button
                  onClick={() => launchWhatsAppFlow()}
                  disabled={createConnection.isLoading}
                  className="min-h-11 w-full shrink-0 bg-green-500 text-white hover:bg-green-600 md:w-auto"
                >
                  {createConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
                  Conectar
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-gray-200 p-4 transition-all hover:border-blue-500 hover:shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start">
                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <p className="font-bold">Modo coexistencia</p>
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-800">Beta</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Te permite usar la app de WhatsApp Business en tu celular y darle a Palomma
                    control sobre el mismo numero para que la gestion se haga desde una sola linea.
                  </p>
                </div>
                <Button
                  onClick={() => launchWhatsAppFlow("whatsapp_business_app_onboarding")}
                  disabled={createConnection.isLoading}
                  className="min-h-11 w-full shrink-0 bg-blue-600 text-white hover:bg-blue-700 md:w-auto"
                >
                  {createConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
                  Conectar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Configuration Section - Only for Palomma users */}
      {isPalommaUser && (
        <>
          <div className="mt-12 border-t border-gray-200 pt-8">
            <div className="flex flex-col gap-4">
              <p className="text-lg font-bold">
                Configuracion avanzada
              </p>
              <p className="text-sm text-gray-600">
                Si ya tienes una cuenta de WhatsApp Business configurada, puedes conectar numeros
                existentes manualmente.
              </p>
              <div className="flex flex-col flex-wrap gap-3 md:flex-row">
                <Button variant="outline" onClick={handleOpenAdvancedConfig} className="min-h-11 w-full md:w-auto">
                  Ver numeros disponibles
                </Button>
                <Button
                  variant="outline"
                  className="min-h-11 w-full border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white md:w-auto"
                  onClick={() =>
                    window.open(
                      "https://console.twilio.com/us1/develop/phone-numbers/manage/search?isoCountry=US&types[]=Local&types[]=Mobile&types[]=Tollfree&capabilities[]=Fax&capabilities[]=Mms&capabilities[]=Sms&capabilities[]=Voice&searchTerm=&searchFilter=left&searchType=number",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Comprar numero de telefono
                </Button>
                <Button
                  variant="outline"
                  className="min-h-11 w-full border-green-500 text-green-600 hover:bg-green-500 hover:text-white md:w-auto"
                  onClick={() =>
                    window.open(
                      "https://business.facebook.com/latest/settings/whatsapp_account?business_id=942845960206288",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Configurar numero en Meta
                </Button>
              </div>
            </div>
          </div>

          {/* Available Phone Numbers Modal */}
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-3xl">
              <DialogHeader>
                <DialogTitle>Numeros de WhatsApp disponibles</DialogTitle>
              </DialogHeader>
              <div className="pb-6">
                {isLoadingPhoneNumbers ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : availablePhoneNumbers && !availablePhoneNumbers.hasCredentials ? (
                  <Alert status="warning">
                    <div className="flex flex-col gap-2">
                      <p className="font-bold">Credenciales no configuradas</p>
                      <p className="text-sm">
                        No se encontro un WhatsApp Business Account ID o token de acceso en tu
                        cuenta. Primero conecta una cuenta usando uno de los flujos de conexion
                        anteriores.
                      </p>
                    </div>
                  </Alert>
                ) : availablePhoneNumbers && availablePhoneNumbers.phoneNumbers.length === 0 ? (
                  <Alert status="info">
                    <p className="text-sm">
                      No se encontraron numeros de telefono disponibles en tu cuenta de WhatsApp
                      Business.
                    </p>
                  </Alert>
                ) : (
                  <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                      Estos son los numeros disponibles en tu cuenta de WhatsApp Business:
                    </p>

                    {/* Mobile card view */}
                    <div className="md:hidden">
                      {availablePhoneNumbers?.phoneNumbers.map((phone, index) => (
                        <div
                          key={phone.id}
                          className={cn(
                            "p-3",
                            index < (availablePhoneNumbers?.phoneNumbers.length ?? 0) - 1 && "border-b border-gray-200"
                          )}
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <span className="font-mono text-sm font-medium">
                              {phone.displayPhoneNumber}
                            </span>
                            <span className={cn("rounded-full px-2 py-0.5 text-xs", phone.codeVerificationStatus === "VERIFIED" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800")}>
                              {phone.codeVerificationStatus}
                            </span>
                          </div>
                          <p className="mb-2 text-xs text-gray-600">
                            {phone.verifiedName}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className={cn(
                              "rounded-full px-2 py-0.5 text-xs",
                              phone.qualityRating === "GREEN" ? "bg-green-100 text-green-800"
                                : phone.qualityRating === "YELLOW" ? "bg-yellow-100 text-yellow-800"
                                : phone.qualityRating === "RED" ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            )}>
                              Calidad: {phone.qualityRating}
                            </span>
                            <Button
                              size="sm"
                              onClick={() =>
                                handleConnectPhone(phone.id, phone.displayPhoneNumber, phone.verifiedName)
                              }
                              disabled={createFromExistingPhone.isLoading}
                              className="min-h-11 bg-green-500 text-white hover:bg-green-600"
                            >
                              {createFromExistingPhone.isLoading && <Loader2 className="size-4 animate-spin" />}
                              Conectar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop table view */}
                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Numero</th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Nombre verificado</th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Estado</th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Calidad</th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Accion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availablePhoneNumbers?.phoneNumbers.map((phone) => (
                            <tr key={phone.id} className="border-b border-gray-100">
                              <td className="px-3 py-2 font-mono text-sm">
                                {phone.displayPhoneNumber}
                              </td>
                              <td className="px-3 py-2">{phone.verifiedName}</td>
                              <td className="px-3 py-2">
                                <span className={cn("rounded-full px-2 py-0.5 text-xs", phone.codeVerificationStatus === "VERIFIED" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800")}>
                                  {phone.codeVerificationStatus}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className={cn(
                                  "rounded-full px-2 py-0.5 text-xs",
                                  phone.qualityRating === "GREEN" ? "bg-green-100 text-green-800"
                                    : phone.qualityRating === "YELLOW" ? "bg-yellow-100 text-yellow-800"
                                    : phone.qualityRating === "RED" ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                                )}>
                                  {phone.qualityRating}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleConnectPhone(
                                      phone.id,
                                      phone.displayPhoneNumber,
                                      phone.verifiedName
                                    )
                                  }
                                  disabled={createFromExistingPhone.isLoading}
                                  className="bg-green-500 text-white hover:bg-green-600"
                                >
                                  {createFromExistingPhone.isLoading && <Loader2 className="size-4 animate-spin" />}
                                  Conectar
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Confirmation Dialog for Toggling Connection Status */}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {connectionToToggle?.disabled ? "Deshabilitar" : "Habilitar"} Conexion de WhatsApp
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {connectionToToggle && connectionToToggle.disabled ? (
                  <div className="flex flex-col gap-3">
                    <p>
                      Estas seguro que deseas <strong>deshabilitar</strong> la conexion de WhatsApp{" "}
                      <strong>{connectionToToggle.phoneNumber}</strong>?
                    </p>
                    <Alert status="warning">
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-bold">
                          Importante:
                        </p>
                        <p className="text-sm">
                          Los mensajes seguiran recibiendose y guardandose en el sistema
                        </p>
                        <p className="text-sm">
                          El agente de IA NO respondera automaticamente a los mensajes
                        </p>
                        <p className="text-sm">No se enviaran seguimientos automaticos</p>
                        <p className="mt-2 text-sm">
                          Esta accion quedara registrada con tu correo electronico:{" "}
                          <strong>{userInfo?.email}</strong>
                        </p>
                      </div>
                    </Alert>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p>
                      Estas seguro que deseas <strong>habilitar</strong> la conexion de WhatsApp{" "}
                      <strong>{connectionToToggle?.phoneNumber}</strong>?
                    </p>
                    <Alert status="info">
                      <p className="text-sm">
                        El agente de IA volvera a responder automaticamente a los mensajes y enviara
                        seguimientos.
                      </p>
                    </Alert>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmToggle}
              disabled={setDisabled.isLoading}
              className={cn(
                connectionToToggle?.disabled
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-green-500 text-white hover:bg-green-600"
              )}
            >
              {setDisabled.isLoading && <Loader2 className="size-4 animate-spin" />}
              {connectionToToggle?.disabled ? "Deshabilitar" : "Habilitar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainSectionSection>
  );
};

export default WhatsappConnectionsContainer;
